import React, { useEffect, useRef, useState } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const PAGE_SIZE = 50
const MAX_SELECT = 500
const REPORT_MIN = 40
const REASON_MIN = 20

type Lane = 'ready' | 'held' | 'purged'

interface DormantRow {
  user_id: number
  username: string
  email: string
  full_name: string | null
  account_status: string | null
  created_at: string
  last_login_at: string | null
  sponsor_user_id: number | null
  sponsor_username: string | null
  block_reason: string | null
}

interface PurgedRow {
  user_id: number
  username: string
  email: string
  full_name: string | null
  purged_at: string
  purge_reason: string | null
  investigation_report: string | null
  sponsor_user_id: number | null
  sponsor_username: string | null
  sponsor_email: string | null
  request_message: string | null
  request_at: string | null
}

interface ChosenAccount {
  id: number
  username: string
}

interface MoveProgress {
  id: number
  username: string
  state: 'waiting' | 'moving' | 'moved' | 'refused' | 'failed' | 'stopped'
  detail: string
}

const DormantAccountPurge: React.FC = () => {
  const [lane, setLane] = useState<Lane>('ready')
  const [query, setQuery] = useState('')
  const [rows, setRows] = useState<DormantRow[]>([])
  const [purgedRows, setPurgedRows] = useState<PurgedRow[]>([])
  const [total, setTotal] = useState(0)
  const [readyTotal, setReadyTotal] = useState(0)
  const [heldTotal, setHeldTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<ChosenAccount[]>([])
  const [heldId, setHeldId] = useState<number | null>(null)
  const [purgedId, setPurgedId] = useState<number | null>(null)
  const [report, setReport] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [purging, setPurging] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [moves, setMoves] = useState<MoveProgress[]>([])
  const stopRef = useRef(false)

  const load = async (nextLane: Lane, nextOffset: number, nextQuery = query) => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({
        q: nextQuery.trim(),
        lane: nextLane,
        limit: String(PAGE_SIZE),
        offset: String(nextOffset),
      })
      const response = await fetch(`${API_BASE_URL}/api/admin/dormant-accounts?${params}`, {
        headers: { ...getSessionHeaders() },
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Search failed')
      if (nextLane === 'purged') {
        setPurgedRows(Array.isArray(body.rows) ? body.rows : [])
        setRows([])
      } else {
        setRows(Array.isArray(body.rows) ? body.rows : [])
        setPurgedRows([])
        setReadyTotal(Number(body.readyTotal) || 0)
        setHeldTotal(Number(body.heldTotal) || 0)
      }
      setTotal(Number(body.total) || 0)
      setOffset(nextOffset)
      setLane(nextLane)
    } catch (err) {
      setRows([])
      setPurgedRows([])
      setTotal(0)
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load('ready', 0, '')
    // The first view is the ready-to-remove list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const switchLane = (nextLane: Lane) => {
    if (purging) return
    setSelected([])
    setHeldId(null)
    setPurgedId(null)
    setReport('')
    setReason('')
    setMoves([])
    setNotice('')
    setConfirming(false)
    load(nextLane, 0, query)
  }

  const selectedIds = selected.map((account) => account.id)
  const heldRow = rows.find((row) => row.user_id === heldId) || null
  const purgedRow = purgedRows.find((row) => row.user_id === purgedId) || null
  const paymentHold = heldRow?.block_reason === 'has a payment record'

  const toggle = (row: DormantRow) => {
    if (row.block_reason || purging) return
    setSelected((current) => {
      if (current.some((account) => account.id === row.user_id)) {
        return current.filter((account) => account.id !== row.user_id)
      }
      if (current.length >= MAX_SELECT) return current
      return [...current, { id: row.user_id, username: row.username }]
    })
  }

  const selectableOnPage = rows.filter((row) => !row.block_reason)

  const togglePage = () => {
    if (purging) return
    const pageIds = selectableOnPage.map((row) => row.user_id)
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))
    if (allSelected) {
      setSelected((current) => current.filter((account) => !pageIds.includes(account.id)))
      return
    }
    setSelected((current) => {
      const next = [...current]
      for (const row of selectableOnPage) {
        if (next.length >= MAX_SELECT) break
        if (!next.some((account) => account.id === row.user_id)) {
          next.push({ id: row.user_id, username: row.username })
        }
      }
      return next
    })
  }

  const markMove = (id: number, state: MoveProgress['state'], detail: string) => {
    setMoves((current) => current.map((item) => (item.id === id ? { ...item, state, detail } : item)))
  }

  const purgeReady = async () => {
    const queue = [...selected]
    stopRef.current = false
    setPurging(true)
    setError('')
    setMoves(queue.map((account) => ({ id: account.id, username: account.username, state: 'waiting', detail: 'Waiting' })))
    const movedIds: number[] = []
    try {
      for (const account of queue) {
        if (stopRef.current) {
          markMove(account.id, 'stopped', 'Stopped before this account was moved')
          continue
        }
        markMove(account.id, 'moving', 'Moving into users_purged')
        try {
          const response = await fetch(`${API_BASE_URL}/api/admin/dormant-accounts`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
            body: JSON.stringify({ action: 'purge', userIds: [account.id] }),
          })
          const body = await response.json()
          if (!response.ok) {
            markMove(account.id, 'failed', body.error || 'Could not move this account')
            setError(body.error || 'Delete failed')
            stopRef.current = true
            continue
          }
          const result = Array.isArray(body.results) ? body.results.find((row: { id?: number }) => row.id === account.id) : null
          if (result?.ok) {
            movedIds.push(account.id)
            markMove(account.id, 'moved', 'Moved to users_purged')
          } else {
            markMove(account.id, 'refused', result?.reason || 'Not removed')
          }
        } catch (err) {
          markMove(account.id, 'failed', err instanceof Error ? err.message : 'Could not move this account')
          setError(err instanceof Error ? err.message : 'Delete failed')
          stopRef.current = true
        }
      }
    } finally {
      setSelected((current) => current.filter((account) => !movedIds.includes(account.id)))
      setPurging(false)
      setConfirming(false)
      await load('ready', offset)
    }
  }

  const purgeHeld = async () => {
    if (!heldRow || paymentHold) return
    setPurging(true)
    setError('')
    setNotice('')
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dormant-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ action: 'review', userId: heldRow.user_id, report: report.trim() }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not remove the account')
      const result = Array.isArray(body.results) ? body.results[0] : null
      if (!result?.ok) throw new Error(result?.reason || 'Could not remove the account')
      setNotice(`${heldRow.username} was moved to users_purged.`)
      setHeldId(null)
      setReport('')
      setConfirming(false)
      await load('held', offset)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the account')
    } finally {
      setPurging(false)
    }
  }

  const reinstate = async () => {
    if (!purgedRow) return
    setPurging(true)
    setError('')
    setNotice('')
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dormant-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ action: 'reinstate', userId: purgedRow.user_id, reason: reason.trim() }),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Could not put the account back')
      const result = Array.isArray(body.results) ? body.results[0] : null
      if (!result?.ok) throw new Error(result?.reason || 'Could not put the account back')
      setNotice(`${purgedRow.username} is back in the live member list.`)
      setPurgedId(null)
      setReason('')
      setConfirming(false)
      await load('purged', offset)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not put the account back')
    } finally {
      setPurging(false)
    }
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const finished = moves.filter((item) => item.state !== 'waiting' && item.state !== 'moving').length
  const movedCount = moves.filter((item) => item.state === 'moved').length
  const progress = moves.length === 0 ? 0 : Math.round((finished / moves.length) * 100)
  const reportReady = report.trim().length >= REPORT_MIN
  const reasonReady = reason.trim().length >= REASON_MIN

  return (
    <div style={{ color: '#fff', padding: '8px 4px 32px' }}>
      <h2 style={{ fontSize: '22px', margin: '0 0 8px' }}>Dormant accounts</h2>
      <p style={{ color: '#d1d5db', maxWidth: '820px', lineHeight: 1.5 }}>
        Ready accounts have no downline, no shares, and no commission, and can be removed together.
        Held accounts are reviewed one at a time. A payment record cannot be removed.
        Removed accounts can be searched and put back after a written reason.
      </p>

      <div style={{ display: 'flex', gap: '8px', margin: '16px 0', flexWrap: 'wrap' }}>
        <TabButton active={lane === 'ready'} color="#166534" label={`Ready to remove${readyTotal ? ` (${readyTotal.toLocaleString()})` : ''}`} onClick={() => switchLane('ready')} />
        <TabButton active={lane === 'held'} color="#92400e" label={`Needs review${heldTotal ? ` (${heldTotal.toLocaleString()})` : ''}`} onClick={() => switchLane('held')} />
        <TabButton active={lane === 'purged'} color="#1e3a8a" label="Removed accounts" onClick={() => switchLane('purged')} />
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (purging) return
          setSelected([])
          setMoves([])
          load(lane, 0, query)
        }}
        style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}
      >
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={lane === 'purged' ? 'Search removed account, sponsor, email, or id' : 'Leave empty for all, or search id, username, email, or name'}
          style={inputStyle}
        />
        <button type="submit" disabled={loading || purging} style={buttonStyle('#FFD700', '#111')}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error ? <p style={{ color: '#fca5a5' }}>{error}</p> : null}
      {notice ? <p style={{ color: '#86efac' }}>{notice}</p> : null}

      {lane === 'ready' ? (
        <ReadyPanel
          rows={rows}
          total={total}
          selected={selected}
          selectedIds={selectedIds}
          selectableOnPage={selectableOnPage}
          purging={purging}
          moves={moves}
          progress={progress}
          finished={finished}
          movedCount={movedCount}
          onToggle={toggle}
          onTogglePage={togglePage}
          onDelete={() => setConfirming(true)}
        />
      ) : null}

      {lane === 'held' ? (
        <div>
          <p style={{ color: '#fcd34d' }}>{total.toLocaleString()} accounts need a review. Select one. A payment record cannot be removed.</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#fcd34d' }}>
                  <th style={cellStyle}>Choose</th>
                  <th style={cellStyle}>Id</th>
                  <th style={cellStyle}>Username</th>
                  <th style={cellStyle}>Email</th>
                  <th style={cellStyle}>Name</th>
                  <th style={cellStyle}>Created</th>
                  <th style={cellStyle}>Sponsor</th>
                  <th style={cellStyle}>Hold</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.user_id} style={{ borderTop: '1px solid #333', background: heldId === row.user_id ? '#3f2a12' : 'transparent' }}>
                    <td style={cellStyle}>
                      <input
                        type="radio"
                        name="held-account"
                        aria-label={`Review ${row.username}`}
                        checked={heldId === row.user_id}
                        onChange={() => { setHeldId(row.user_id); setReport(''); setNotice('') }}
                      />
                    </td>
                    <td style={cellStyle}>{row.user_id}</td>
                    <td style={cellStyle}>{row.username}</td>
                    <td style={cellStyle}>{row.email}</td>
                    <td style={cellStyle}>{row.full_name || ''}</td>
                    <td style={cellStyle}>{row.created_at ? row.created_at.slice(0, 10) : ''}</td>
                    <td style={cellStyle}>{row.sponsor_username || ''}</td>
                    <td style={cellStyle}><HoldBadge reason={row.block_reason} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {heldRow ? (
            <div style={{ marginTop: '16px', maxWidth: '720px', border: '1px solid #92400e', borderRadius: '12px', padding: '16px', background: '#1c1408' }}>
              <h3 style={{ marginTop: 0 }}>Review {heldRow.username}</h3>
              <p style={{ color: '#fde68a' }}>
                Investigate this account, then write why it is being removed. The confirm button stays off until that report is written.
                {paymentHold ? ' This account has a payment record, so it cannot be removed.' : ' The KYC or support record is kept with the archived account and restored if the account is put back.'}
              </p>
              <textarea
                value={report}
                onChange={(event) => setReport(event.target.value)}
                placeholder="What did you check, and why is this account being removed?"
                style={{ ...inputStyle, maxWidth: '100%', minHeight: '120px', width: '100%' }}
              />
              <div style={{ color: reportReady ? '#86efac' : '#fca5a5', fontSize: '13px', margin: '6px 0 12px' }}>
                {report.trim().length} / {REPORT_MIN} characters
              </div>
              <button
                type="button"
                disabled={!reportReady || paymentHold || purging}
                onClick={() => setConfirming(true)}
                style={buttonStyle(!reportReady || paymentHold ? '#555' : '#b45309', '#fff')}
              >
                Confirm removal
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {lane === 'purged' ? (
        <div>
          <p style={{ color: '#93c5fd' }}>{total.toLocaleString()} removed accounts. The sponsor can contact the person and ask for the account to be put back.</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#93c5fd' }}>
                  <th style={cellStyle}>Choose</th>
                  <th style={cellStyle}>Id</th>
                  <th style={cellStyle}>Username</th>
                  <th style={cellStyle}>Email</th>
                  <th style={cellStyle}>Removed</th>
                  <th style={cellStyle}>Sponsor</th>
                  <th style={cellStyle}>Sponsor email</th>
                  <th style={cellStyle}>Request</th>
                </tr>
              </thead>
              <tbody>
                {purgedRows.map((row) => (
                  <tr key={row.user_id} style={{ borderTop: '1px solid #333', background: purgedId === row.user_id ? '#172554' : 'transparent' }}>
                    <td style={cellStyle}>
                      <input
                        type="radio"
                        name="purged-account"
                        aria-label={`Restore ${row.username}`}
                        checked={purgedId === row.user_id}
                        onChange={() => {
                          setPurgedId(row.user_id)
                          setReason(row.request_message || '')
                          setNotice('')
                        }}
                      />
                    </td>
                    <td style={cellStyle}>{row.user_id}</td>
                    <td style={cellStyle}>{row.username}</td>
                    <td style={cellStyle}>{row.email}</td>
                    <td style={cellStyle}>{row.purged_at ? row.purged_at.slice(0, 10) : ''}</td>
                    <td style={cellStyle}>{row.sponsor_username || ''}</td>
                    <td style={cellStyle}>{row.sponsor_email || ''}</td>
                    <td style={cellStyle}>{row.request_message ? 'Sponsor asked' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {purgedRow ? (
            <div style={{ marginTop: '16px', maxWidth: '720px', border: '1px solid #1e3a8a', borderRadius: '12px', padding: '16px', background: '#0b1220' }}>
              <h3 style={{ marginTop: 0 }}>Put {purgedRow.username} back</h3>
              {purgedRow.investigation_report ? <p style={{ color: '#d1d5db' }}>Removal report: {purgedRow.investigation_report}</p> : null}
              {purgedRow.request_message ? <p style={{ color: '#fde68a' }}>Sponsor request: {purgedRow.request_message}</p> : null}
              <p style={{ color: '#93c5fd' }}>
                Contact for this person: {purgedRow.email}
                {purgedRow.sponsor_email ? `. Sponsor contact: ${purgedRow.sponsor_email}` : ''}
              </p>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Why is this account being put back?"
                style={{ ...inputStyle, maxWidth: '100%', minHeight: '100px', width: '100%' }}
              />
              <div style={{ color: reasonReady ? '#86efac' : '#fca5a5', fontSize: '13px', margin: '6px 0 12px' }}>
                {reason.trim().length} / {REASON_MIN} characters
              </div>
              <button type="button" disabled={!reasonReady || purging} onClick={() => setConfirming(true)} style={buttonStyle(reasonReady ? '#1d4ed8' : '#555', '#fff')}>
                Approve reinstatement
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
        <button type="button" disabled={offset === 0 || loading || purging} onClick={() => load(lane, Math.max(0, offset - PAGE_SIZE))} style={buttonStyle('#333', '#fff')}>Previous</button>
        <span>Page {page} of {pages}</span>
        <button type="button" disabled={offset + PAGE_SIZE >= total || loading || purging} onClick={() => load(lane, offset + PAGE_SIZE)} style={buttonStyle('#333', '#fff')}>Next</button>
      </div>

      {confirming ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#161616', border: '1px solid #444', borderRadius: '12px', padding: '20px', maxWidth: '560px', width: '92%' }}>
            {lane === 'ready' ? (
              <>
                <h3 style={{ marginTop: 0 }}>Remove {selected.length} ready {selected.length === 1 ? 'account' : 'accounts'}?</h3>
                <p style={{ color: '#d1d5db' }}>Each account is copied into users_purged and then removed from the live list. The bar names the account being moved.</p>
                {moves.length > 0 ? <ProgressList moves={moves} progress={progress} movedCount={movedCount} finished={finished} /> : null}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  {purging ? (
                    <button type="button" onClick={() => { stopRef.current = true }} style={buttonStyle('#333', '#fff')}>Stop after this account</button>
                  ) : (
                    <button type="button" onClick={() => setConfirming(false)} style={buttonStyle('#333', '#fff')}>{moves.length > 0 ? 'Close' : 'Cancel'}</button>
                  )}
                  <button type="button" onClick={purgeReady} disabled={purging || selected.length === 0} style={buttonStyle(purging || selected.length === 0 ? '#555' : '#b91c1c', '#fff')}>
                    {purging ? 'Moving…' : 'Delete'}
                  </button>
                </div>
              </>
            ) : null}
            {lane === 'held' && heldRow ? (
              <>
                <h3 style={{ marginTop: 0 }}>Remove {heldRow.username}?</h3>
                <p style={{ color: '#d1d5db' }}>{report.trim()}</p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setConfirming(false)} disabled={purging} style={buttonStyle('#333', '#fff')}>Cancel</button>
                  <button type="button" onClick={purgeHeld} disabled={purging || !reportReady || paymentHold} style={buttonStyle('#b45309', '#fff')}>
                    {purging ? 'Moving…' : 'Confirm removal'}
                  </button>
                </div>
              </>
            ) : null}
            {lane === 'purged' && purgedRow ? (
              <>
                <h3 style={{ marginTop: 0 }}>Put {purgedRow.username} back into the live member list?</h3>
                <p style={{ color: '#d1d5db' }}>{reason.trim()}</p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setConfirming(false)} disabled={purging} style={buttonStyle('#333', '#fff')}>Cancel</button>
                  <button type="button" onClick={reinstate} disabled={purging || !reasonReady} style={buttonStyle('#1d4ed8', '#fff')}>
                    {purging ? 'Restoring…' : 'Approve'}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ReadyPanel(props: {
  rows: DormantRow[]
  total: number
  selected: ChosenAccount[]
  selectedIds: number[]
  selectableOnPage: DormantRow[]
  purging: boolean
  moves: MoveProgress[]
  progress: number
  finished: number
  movedCount: number
  onToggle: (row: DormantRow) => void
  onTogglePage: () => void
  onDelete: () => void
}) {
  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ color: '#86efac' }}>{props.total.toLocaleString()} ready accounts</span>
        <span>{props.selected.length} / {MAX_SELECT} selected</span>
        <button type="button" disabled={props.selected.length === 0 || props.purging} onClick={props.onDelete} style={buttonStyle(props.selected.length === 0 ? '#555' : '#b91c1c', '#fff')}>
          Delete selected
        </button>
      </div>
      {props.moves.length > 0 ? <ProgressList moves={props.moves} progress={props.progress} movedCount={props.movedCount} finished={props.finished} /> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#86efac' }}>
              <th style={cellStyle}>
                <input type="checkbox" aria-label="Select accounts on this page" checked={props.selectableOnPage.length > 0 && props.selectableOnPage.every((row) => props.selectedIds.includes(row.user_id))} onChange={props.onTogglePage} disabled={props.purging} />
              </th>
              <th style={cellStyle}>Id</th>
              <th style={cellStyle}>Username</th>
              <th style={cellStyle}>Email</th>
              <th style={cellStyle}>Name</th>
              <th style={cellStyle}>Status</th>
              <th style={cellStyle}>Created</th>
              <th style={cellStyle}>Sponsor</th>
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => {
              const checked = props.selectedIds.includes(row.user_id)
              const atCap = !checked && props.selected.length >= MAX_SELECT
              return (
                <tr key={row.user_id} style={{ borderTop: '1px solid #333' }}>
                  <td style={cellStyle}>
                    <input type="checkbox" aria-label={`Select ${row.username}`} checked={checked} disabled={atCap || props.purging} onChange={() => props.onToggle(row)} />
                  </td>
                  <td style={cellStyle}>{row.user_id}</td>
                  <td style={cellStyle}>{row.username}</td>
                  <td style={cellStyle}>{row.email}</td>
                  <td style={cellStyle}>{row.full_name || ''}</td>
                  <td style={cellStyle}>{row.account_status || ''}</td>
                  <td style={cellStyle}>{row.created_at ? row.created_at.slice(0, 10) : ''}</td>
                  <td style={cellStyle}>{row.sponsor_username || ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ProgressList(props: { moves: MoveProgress[]; progress: number; movedCount: number; finished: number }) {
  return (
    <div style={{ marginBottom: '16px', maxWidth: '720px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span>{props.movedCount} moved</span>
        <span>{props.finished} of {props.moves.length}</span>
      </div>
      <div style={{ background: '#333', borderRadius: '8px', height: '14px', overflow: 'hidden' }} role="progressbar" aria-valuenow={props.progress} aria-valuemin={0} aria-valuemax={100}>
        <div style={{ width: `${props.progress}%`, height: '100%', background: '#FFD700' }} />
      </div>
      <div style={{ maxHeight: '220px', overflowY: 'auto', marginTop: '8px' }}>
        {props.moves.map((item) => (
          <div key={item.id} style={{ color: moveColor(item.state), padding: '2px 0' }}>{item.username} ({item.id}): {item.detail}</div>
        ))}
      </div>
    </div>
  )
}

function HoldBadge({ reason }: { reason: string | null }) {
  const color = reason === 'has a payment record' ? '#fecaca' : reason === 'has KYC on file' ? '#fde68a' : '#bfdbfe'
  const background = reason === 'has a payment record' ? '#7f1d1d' : reason === 'has KYC on file' ? '#78350f' : '#1e3a8a'
  return <span style={{ background, color, borderRadius: '999px', padding: '3px 8px', fontSize: '12px' }}>{reason || 'Held'}</span>
}

function TabButton(props: { active: boolean; color: string; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={props.onClick} style={{
      background: props.active ? props.color : '#111',
      color: '#fff',
      border: `1px solid ${props.color}`,
      borderRadius: '999px',
      padding: '8px 14px',
      fontWeight: 700,
      cursor: 'pointer',
    }}>
      {props.label}
    </button>
  )
}

const cellStyle: React.CSSProperties = { padding: '8px 10px', whiteSpace: 'nowrap' }
const inputStyle: React.CSSProperties = {
  flex: 1,
  maxWidth: '460px',
  background: '#111',
  color: '#fff',
  border: '1px solid #444',
  borderRadius: '8px',
  padding: '10px 12px',
}

function buttonStyle(background: string, color: string): React.CSSProperties {
  return { background, color, border: 'none', borderRadius: '8px', padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }
}

function moveColor(state: MoveProgress['state']): string {
  if (state === 'moved') return '#86efac'
  if (state === 'moving') return '#FFD700'
  if (state === 'waiting' || state === 'stopped') return '#d1d5db'
  return '#fca5a5'
}

export default DormantAccountPurge
